<template>
<div class="main list-view">

	<div class="sub-header">
		<div v-if="!this.showReviewOrder" class="input-with-control">
			<input type="search" class="search" placeholder="Search" v-model="filters.search_term" @search="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div v-if="!this.showReviewOrder" class="controls">
			<pagination :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div v-if="!this.showReviewOrder" class="filters">

		<div v-if="distributorId" class="title">Ordering from...</div>
		<div v-if="distributorId" class="divider"></div>
		<div v-for="(distributor, index) in distributors" :class="['filter-list-button-large', { active: distributorId == distributor.distributor_id, disabled: loading }]" @click="loading ? null : getDistributor(distributor.distributor_id)">{{ distributor.label }}</div>
		<div class="divider"></div>
		<div :class="['filter-list-button-large', { active: distributorId == null, disabled: loading }]" @click="loading ? null : getDistributor(null)">Other Suppliers</div>
		<div class="divider"></div>

		<div v-if="!this.filters.save_for_later && distributorId">
			<label>Subtotal ({{ selectedDistributorQtyTotal }} items): ${{ selectedDistributorItemCost }}</label>
			<div :class="['button', hasSelectedCurrent && distributorId && !showReviewOrder ? 'action' : 'disabled']" @click="hasSelectedCurrent ? reviewOrder() : null">Review {{ distributorLabelFromId }} Order</div>
		</div>

		<div v-if="distributorId" class="divider"></div>
		<div class="title">Filter Cart</div>

		<label>Department</label>
		<select v-model="filters.department_id" @change="getList()">
			<option value="">Select...</option>
			<option v-for="(department, index) in departments" :value="department.department_id">{{ department.description }}</option>
		</select>
		<div v-if="distributorId">
			<input type="radio" name="stock" v-model="filters.stock" value="all" @change="getList()">
			<label style="padding: 0 0 0 0.5em;">Both In / Out of Stock</label>
		</div>
		<div v-if="distributorId">
			<input type="radio" name="stock" v-model="filters.stock" value="in_stock" @change="getList()">
			<label style="padding: 0 0 0 0.5em;">In Stock Only</label>
		</div>
		<div v-if="distributorId">
			<input type="radio" name="stock" v-model="filters.stock" value="out_of_stock" @change="getList()">
			<label style="padding: 0 0 0 0.5em;">Out of Stock Only</label>
		</div>
		<div>
			<input type="radio" name="stock" v-model="filters.stock" value="selected" >
			<label style="padding: 0 0 0 0.5em;">Selected Only</label>
		</div>

		<div :class="['button', hasFilters ? 'neutral' : 'disabled']" @click="hasFilters ? clearFilters() : null">Clear Filters</div>

		<template v-if="storeAdministrator && managerCarts.length > 1">
			<div class="divider"></div>
			<div class="title">Manager Carts</div>
			<label>Viewing this cart...</label>
			<select v-model="filters.cart_user_id" @change="getList()">
				<option value="">Select...</option>
				<option v-for="(cart, index) in managerCarts" :value="cart.user_id">{{ cart.user_name }} ({{ cart.cart_count }})</option>
			</select>
			<div @click="toggleModalMergeReorderCart()" :class="['button', (saving || !filters.cart_user_id) ? 'disabled' : 'neutral']">Merge Carts</div>
		</template>

		<div class="divider"></div>
		<div @click="toggleModalReorderCartSettings()" class="extra-menu-item">Reorder Cart Settings</div>
		<router-link v-if="legacyLink" :to="legacyLink" class="extra-menu-item">Legacy Version</router-link>
	</div>

    <loading v-if="loading"></loading>

	<div v-else-if="showReviewOrder" class="list review-order">
		<h1>Order Review for {{ orderDistributor.company_name }}</h1>
		<div class="three-columns">
			<div class="review-order-section">
				<h2>Distributor</h2>
				<div>{{ orderDistributor.company_name }}</div>
				<div>{{ orderDistributor.address_1 }}</div>
				<div v-if="orderDistributor.address_2">{{ orderDistributor.address_2 }}</div>
				<div>{{ orderDistributor.city }}, {{ orderDistributor.state }} {{ orderDistributor.zip_code }}</div>
			</div>
			<div class="review-order-section">
				<h2>Ship To</h2>
				<div>{{ orderShipTo.company_name }}</div>
				<div>{{ orderShipTo.address_1 }}</div>
				<div v-if="orderShipTo.address_2">{{ orderShipTo.address_2 }}</div>
				<div style="margin: 0 0 0.5em 0;">{{ orderShipTo.city }}, {{ orderShipTo.state }} {{ orderShipTo.zip_code }}</div>
				<div class="button delete-large smaller" title="Edit ship to" @click="editingShipTo = Object.assign({}, orderShipTo); showEditShipTo = true"><i class="fas fa-edit"></i></div>
			</div>
			<div class="review-order-section">
				<h2>Summary</h2>
				<table>
					<tr><td class="label">Total Cost</td><td class="right">${{ orderTotalFrom(reviewList,'cost') }}</td></tr>
					<tr><td class="label">Total Retail</td><td class="right">${{ orderTotalFrom(reviewList, 'price') }}</td></tr>
					<tr><td class="label">Total Margin</td><td class="right">${{ orderTotalFrom(reviewList, 'margin') }}</td></tr>
				</table>
			</div>
		</div>
		<table class="list order-data-grid">
			<thead>
				<tr>
					<th>Item</th><th>Item Number</th><th class="right">Cost</th><th class="right">Qty</th><th class="right">Extended Cost</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(item, index) in reviewList" :key="item.product_id">
					<td>{{ item.description }}</td>
					<td>{{ item.item_number }}</td>
					<td class="right">${{ item.cost }}</td>
					<td class="right">{{ item.quantity }}</td>
					<td class="right">${{ (item.quantity * item.cost).toFixed(2) }}</td>
				</tr>
				<tr><td colspan="4" class="right"><b>Order Total</b></td><td class="right"><b>${{ orderTotalFrom(reviewList,'cost') }}</b></td></tr>
			</tbody>
		</table>

		<div class="two-columns controls">
			<div>
				<div>
					<h3>Summary</h3>
					<label style="margin: 0 0.5em 0 0;">Total Cost</label>${{ orderTotalFrom(reviewList,'cost') }}
					<label style="margin: 0 0.5em 0 1em;">Total Retail</label>${{ orderTotalFrom(reviewList, 'price') }}
					<label style="margin: 0 0.5em 0 1em;">Total Margin</label>${{ orderTotalFrom(reviewList, 'margin') }}
				</div>
 				<div class="note">NOTE: margin does not include distributor shipping charges.</div>
			</div>
			<div style="text-align: right;">
				<div class="button cancel spaced" @click="showReviewOrder = false">Back to Cart</div>
				<div class="button action spaced" @click="startPlaceOrder()">Place Order</div>
			</div>
		</div>

	</div>

	<div v-else>

		<div class="list-nav">
			<div class="nav-tab" :class="{ 'nav-tab-active': !filters.save_for_later }" @click="filters.save_for_later = false">Order Cart</div>
			<div class="nav-tab" :class="{ 'nav-tab-active': filters.save_for_later }" @click="filters.save_for_later = true">Save for Later</div>

			<div v-if="itemsSelected" class="bulk-controls">
				<div v-if="filters.save_for_later" class="bulk-cart-control" @click="openBulkActionsConfirmation('move',getSelectedProductIdsFromList())"><i class="fa-solid fa-cart-shopping"></i> Move Selected to Cart ({{ itemsSelected }})</div>
				<div v-else class="bulk-cart-control" @click="openBulkActionsConfirmation('save',getSelectedProductIdsFromList())"><i class="fa-solid fa-bookmark"></i> Save Selected for Later ({{ itemsSelected }})</div>
				<div class="bulk-cart-control" @click="openBulkActionsConfirmation('remove',getSelectedProductIdsFromList())"><i class="fas fa-trash-alt"></i> Remove Selected ({{ itemsSelected }})</div>
			</div>
		</div>

		<div class="modal confirm-bulk" v-if="confirmBulkActions.show">
			<div class="inner">
				<h2>{{ confirmBulkActions.title }}</h2>
				<div class="content">
					<p class="center" v-html="confirmBulkActions.message"></p>
					<p v-if="saving" class="center" style="font-size: 1.5em;"><i class="fa fa-spinner fa-spin"></i></p>
				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="confirmBulkActions.show = false">Cancel</div>
					<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="executeBulkAction()">{{ confirmBulkActions.actionCall }}</div>
				</div>
			</div>
		</div>

		<div class="list">

			<div v-if="list.length == 0" class="empty-list">
				This {{ filters.save_for_later ? 'save for later' : 'order' }} cart is empty
			</div>

			<div v-if = "displayedList.length" class = "cart-data-grid">
				<div class="cart-data-grid-heading">
					<label class = "checkbox with-outside-label item-checkbox">
						<input type = "checkbox" :checked="allSelectedOnPage" :indeterminate.prop="someSelectedOnPage && !allSelectedOnPage" @change="toggleSelectAll($event.target.checked)">
						<span><i class="fas fa-check-circle"></i></span>

					</label>
					<div class="title">
						{{ allSelectedOnPage ? 'Unselect All' : 'Select All'  }} ({{ displayedList.length }})
					</div>
				</div>
			</div>

			<div v-for="(item, index) in displayedList" class="cart-data-grid">
				<div class="cart-data-grid-heading">
					<label class="checkbox with-outside-label item-checkbox">
						<input type="checkbox" :checked="isSelected(item.product_id)" @change="toggleSelected(item, $event.target.checked)">
						<span><i class="fas fa-check-circle"></i></span>
					</label>
					<div class="title" @click="getDetail(item.product_id)" :class="{unavailabletext: item.available == 0}">{{ item.description }}</div>
				</div>
				<div class="cart-data-image">
					<img :src="item.image_src" @click="getDetail(item.product_id)">
				</div>
				<div class="meta">
					<table class="data-table">
						<tr><td class="label right">ID</td><td class="data">{{ item.product_id }}</td></tr>
						<tr><td class="label right">UPC</td><td class="data">{{ item.upc }}</td></tr>
						<tr v-if="distributorId"><td class="label right">Item</td><td class="data">{{ item.item_number }}</td></tr>
					</table>
				</div>
				<div class="meta">
					<table class="data-table">
						<tr>
							<td class="label right"><span class="clickable" title="Compare costs" @click="getCostCompare(item.product_id)">Cost</span></td>
							<td class="data" style="position: relative;">
								<span class="clickable" title="Compare costs" @click="getCostCompare(item.product_id)">${{ item.cost }}<i v-if="distributorId" class="fa-solid fa-arrows-turn-to-dots" style="margin: 0 0 0 0.5em;"></i></span>
								<div v-if="costCompareItem == item.product_id" class="cart-data-grid-compare">
									<h3>Distributor Cost Compare<span class="close" @click="getCostCompare(item.product_id)">X</span></h3>
									<div v-if="loadingCostCompare" style="padding: 1em; text-align: center;"><i class="fas fa-spinner fa-spin"></i></div>
									<div v-else style="margin: 0.5em;">
										<table class="compare-table">
											<thead>
												<tr><th>Distributor</th><th>Item Number</th><th class="right">Cost</th><th class="right">Available</th></tr>
											</thead>
											<tbody>
												<tr v-for="(cost, index) in costCompare" :class="{'selected': cost.distributor_id == distributorId}">
													<td>{{ cost.label }}</td>
													<td>{{ cost.item_number }}</td>
													<td class="right"><span v-if="cost.cost">$</span>{{ cost.cost }}</td>
													<td class="right">{{ cost.available }}</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</td>
						</tr>
						<tr><td class="label right">Retail</td><td class="data">${{ item.price }}</td></tr>
						<tr><td class="label right">Margin</td><td class="data">${{ item.margin }}</td></tr>
					</table>
				</div>
				<div class="meta">
					<table class="data-table">
						<tr><td class="label right">30 Day Sales</td><td class="data">{{ item.sales }}</td></tr>
						<tr><td class="label right">On Hand</td><td class="data">{{ item.on_hand }}</td></tr>
						<tr>
							<td class="label right">On Order</td>
							<td class="data" style="position: relative;">
								{{ item.on_order }}
								<i v-if="item.on_order > 0" class="fa-solid fa-question clickable" style="position: absolute; right: -0.75em;" @click="showOnOrder(item.product_id)"></i>
								<div v-if="onOrderItem == item.product_id" class="cart-data-grid-on-order">
									<h3>On Order<span class="close" @click="showOnOrder(item.product_id)">X</span></h3>
									<div style="margin: 0.5em;">
										On order quantity is the sum from recent distributor orders and purchase orders that have not yet been received.
									</div>
								</div>

							</td>
						</tr>
					</table>
				</div>
				<div class="meta">
					<table class="data-table">
						<tr><td class="label right">Reorder Point</td><td class="data">{{ item.reorder_point }}</td></tr>
						<tr><td class="label right">Restock Qty</td><td class="data">{{ item.order_quantity }}</td></tr>
						<tr @click="setReorderPoints(item)"><td class="label right clickable">Edit Reorder Point</td><td class="data"><div class="clickable" title="Manage reorder points"><i class="fas fa-edit"></i></div></td></tr>
					</table>
				</div>
				<div class="meta">
					<table class="data-table">
						<tr v-if="distributorId"><td class="label right">Available</td><td class="data" :class="{unavailable: item.available == 0}">{{ item.available }}</td></tr>
						<tr v-if="!filters.save_for_later">
							<td class="label right">Order Qty</td>
							<td class="data"><input type="number" v-model.number="item.quantity" :min="1" :max="item.available" :disabled="item.available < 1 || saving" style="font-size: 1em" @change="(updateSelectedQuantity(item), saveQuantity(item.product_id))"></td>
						</tr>
					</table>
				</div>
				<div class="meta">
					<div v-if="!filters.save_for_later"  style="margin: 0 0 0.5em 0;" @click="manageItemNotes(item.shopping_cart_item_id)">
						<i class="fa-solid fa-note-sticky clickable"></i><label class="clickable" style="padding: 0 0 0 0.5em;">{{ item.notes ? 'Read' : 'Add A' }} Note</label>
					</div>
					<div v-if="filters.save_for_later" style="margin: 0 0 0.5em 0;" @click="setSaveForLater(item.product_id)">
						<i class="fa-solid fa-cart-shopping clickable" title="Move to cart"></i><label class="clickable" style="padding: 0 0 0 0.5em;">Move to Cart</label>
					</div>
					<div v-else style="margin: 0 0 0.5em 0;" @click="setSaveForLater(item.product_id)">
						<i class="fa-solid fa-bookmark clickable" title="Save for later"></i><label class="clickable" style="padding: 0 0 0 0.5em;">Save for Later</label>
					</div>
					<div @click="removeFromCart(item.product_id)">
						<i class="fa-solid fa-trash-alt clickable" title="Remove from cart"></i><label class="clickable" style="padding: 0 0 0 0.5em;">Remove</label>
					</div>
				</div>
			</div>

		</div>

	</div>

	<div class="modal" v-if="showDetail">
		<div class="inner">
			<h2>Product Detail</h2>
			<div class="content">
				<table class="data-table">
					<tr><td class="label right">UPC</td><td class="data">{{ productDetail.upc }}</td></tr>
					<tr><td class="label right">Description</td><td class="data">{{ productDetail.description }}</td></tr>
					<tr><td class="label right">Department</td><td class="data">{{ productDetail.department }}</td></tr>
					<tr><td class="label right">Cateogry</td><td class="data">{{ productDetail.category }}</td></tr>
					<tr><td class="label right">Caliber</td><td class="data">{{ productDetail.caliber }}</td></tr>
					<tr><td class="label right">Manufacturer</td><td class="data">{{ productDetail.manufacturer }}</td></tr>
					<tr><td class="label right">Model</td><td class="data">{{ productDetail.model }}</td></tr>
					<tr><td class="label right">SKU</td><td class="data">{{ productDetail.sku }}</td></tr>
				</table>
			</div>
			<div class="controls">
				<div class="button cancel" @click="showDetail = false">Back To List</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showOrderPoints">
		<div class="inner">
			<h2>Order Points</h2>
			<div class="content">
				<div class="one-three-columns">
					<div>
						<img :src="orderPoints.image_src" style="max-width: 90%;">
					</div>
					<div>
						<b>{{ orderPoints.description }}</b>
						<table class="data-table">
							<tr><td class="label right">ID</td><td class="data" width="33%">{{ orderPoints.product_id }}</td><td class="label right">Cost</td><td class="data">${{ orderPoints.cost }}</td></tr>
							<tr><td class="label right">UPC</td><td class="data" width="33%">{{ orderPoints.upc }}</td><td class="label right">Retail</td><td class="data">${{ orderPoints.price }}</td></tr>
							<tr><td class="label right">Item</td><td class="data" width="33%">{{ orderPoints.item_number }}</td><td class="label right">Margin</td><td class="data">${{ orderPoints.margin }}</td></tr>
						</table>
					</div>
				</div>
				<div class="divider"></div>
				<table class="data-table">
					<tr><td class="label right" width="25%">Reorder Point</td><td class="data"><input type="number" min="0" v-model="orderPoints.reorder_point"></td>
					<td class="label right" width="25%">Restock Quantity</td><td class="data"><input type="number" min="0" v-model="orderPoints.order_quantity"></td></tr>
				</table>
				<p v-if="message" class="note">{{ message }}</p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showOrderPoints = false">Cancel</div>
				<div class="button action spaced" @click="saveOrderPoints()">Save Order Points</div>
			</div>
		</div>
	</div>

	<div class="modal confirm-order" v-if="confirmPlaceOrder">
		<div class="inner">
			<h2>{{ placeOrderHeading }}</h2>
			<div class="content">
				<p class="center" v-html="placeOrderMessage"></p>
				<p v-if="saving" class="center" style="font-size: 1.5em;"><i class="fa fa-spinner fa-spin"></i></p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="confirmPlaceOrder = false">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="placeOrder()">Yes, place order</div>
			</div>
		</div>
	</div>

	<div class="modal manage-notes" v-if="shoppingCartItemId">
		<div class="inner">
			<h2>Manage Notes</h2>
			<div class="content">
				<label>Item</label>
				<p>{{ itemByShoppingCartItemId.description }}</p>
				<label>Note</label>
				<textarea v-model="itemByShoppingCartItemId.notes" rows="5" cols="40"></textarea>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="shoppingCartItemId = ''">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="saveItemNotes()">Save Notes</div>
			</div>
		</div>
	</div>

	<message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" @action="doMessageModalAction()" />

	<div v-if="showReorderCartSettings" class="modal">
		<div class="inner modal-lg">
			<h2>Reorder Cart Settings</h2>
			<div class="content two-columns">
				<div>
					<div class="option-block">
						<input v-model="reorderCartMode" type="radio" id="multiple-carts-setting" name="multiple-carts-setting" value="multi-cart" />
						<label for="multiple-carts-setting">Manager Reorder Carts</label>
					</div>
					<div>
						<p>
							Enabling Manager Reorder Carts means that each of your Manager-level users have their own reorder cart that items can be added to and ordered from. Manager Carts can be merged if needed.
						</p>
					</div>
				</div>
				<div>
					<div class="option-block">
						<input v-model="reorderCartMode" type="radio" id="single-cart-setting" name="single-cart-setting" value="single-cart" />
						<label for="single-cart-setting">Single Reorder Cart</label>
					</div>
					<div>
						<p>
							Enabling a Single Reorder Cart means that any time a Manager-level user adds an item to the reorder cart, all items will show in one single cart to be ordered from.
						</p>
					</div>
				</div>
			</div>
			<p v-if="saving" class="center" style="font-size: 1.5em;"><i class="fa fa-spinner fa-spin"></i></p>
			<div v-if="modalMessage" class="modal-message">{{ modalMessage }}</div>
			<div class="controls">
				<div class="button cancel spaced" @click="toggleModalReorderCartSettings()">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="saveReorderCartSettings()">Save Settings</div>
			</div>
		</div>
	</div>

	<div v-if="showMergeReorderCarts" class="modal" >
		<div class="inner modal-lg">
			<h2>Merge Reorder Carts</h2>
			<div class="content">
				<p v-if="singleCartUserId" class="center">You have a single reorder cart &ndash; merge will move all items from other carts into your reorder cart.<div class="divider"></div></p>
				<div class="two-columns merge-cart">
					<div class="col-1">
						<p>Your reorder carts currently look like this...</p>
						<div v-if="singleCartUserId" class="two-columns header-cart singlez">
							<p class="header-cart-txt"><b>Single Reorder Cart</b></p>
							<p>Items in cart: <b>0</b></p>
						</div>
						<template v-else>
							<div v-for="(manager, index) in managerCarts.filter((manager) => manager.user_id == filters.cart_user_id)">
								<div class="two-columns header-cart">
									<p class="header-cart-txt">Your cart: <b>{{ manager.user_name }}</b></p>
									<p>Items in cart: <b>{{ manager.cart_count }}</b></p>
								</div>
								<p class="header-cart-instructions">Select carts that you want to merge into your cart. Once carts have been merged, they will be emptied.</p>
							</div>
						</template>
						<div v-for="(manager, index) in sortedReorderCarts.filter((cart) => cart.cart_count > 0)">
							<div class="merge-cart-line">
								<div class="user-line">
									<label v-if="!singleCartUserId" class="checkbox with-outside-label item-checkbox">
										<input 
											type="checkbox"
											@change="updateMergeReorderCart(manager, $event.target.checked)"
										/>
										<span><i class="fas fa-check-circle"></i></span>
									</label>
									<p>User: <b>{{ manager.user_name }}</b></p>
								</div>
								<p>Items in cart: <b>{{ manager.cart_count }}</b></p>
							</div>
						</div>
					</div>
					<div class="col-2">
						<p>Once merge is complete, they will look like this...</p>
						<div v-if="singleCartUserId" class="two-columns header-cart">
							<p class="header-cart-txt"><b>Single Reorder Cart</b></p>
							<p>Items in cart: <b>{{ mergeReorderCartInventoryCount }}</b></p>
						</div>
						<template v-else>
							<div v-for="(manager, index) in managerCarts.filter((manager) => manager.user_id == filters.cart_user_id)">
								<div class="two-columns header-cart">
									<p class="header-cart-txt">Your cart: <b>{{ manager.user_name }}</b></p>
									<p>Items in cart: <b>{{ mergeReorderCartInventoryCount }}</b></p>
								</div>
							</div>
						</template>
						<div v-for="(manager, index) in sortedReorderCarts.filter((cart) => cart.cart_count > 0)">
							<div class="merge-cart-line">
								<div class="user-line">
									<p>User: <b>{{ manager.user_name }}</b></p>
								</div>
								<p>Items in cart: <b>{{ mergeReorderCartInventories[manager.user_id] }}</b></p>
							</div>
						</div>
					</div>
				</div>
			</div>
			<p v-if="saving" class="center" style="font-size: 1.5em;"><i class="fa fa-spinner fa-spin"></i></p>
			<p v-if="message" class="center" style="font-size: 1.25em; padding-top: .5em;">{{ message }}</p>
			<div class="controls">
				<div v-if="singleCartUserId" class="button spaced cancel" @click="resetSingleReorderCartMerge()">Cancel Merge</div>
				<div v-else class="button spaced cancel" @click="toggleModalMergeReorderCart()">Cancel Merge</div>
				<div :class="['button', 'spaced', saving || (!singleCartUserId && mergeReorderCartUserIds.size < 1) ? 'disabled' : 'action']" @click="singleCartUserId ? openMergeConfirmation('all') : openMergeConfirmation('selected')">Complete Merge</div>
			</div>
		</div>
	</div>

	<div class = "modal confirm-merge" v-if="confirmMerge.show"> 
		<div class="inner"> 
			<h2>{{ confirmMerge.title }}</h2>
			<div class="content">
				<p class="center" v-html="confirmMerge.message"></p>
				<p v-if="saving" class="center" style="font-size: 1.5em;"><i class="fa fa-spinner fa-spin"></i></p>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="confirmMerge.show = false">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="executeMergeReorderCarts()">{{ confirmMerge.actionCall }}</div>
			</div>
		</div>
	</div>

	<div v-if="showEditShipTo" class="modal edit-ship-to"> 
		<div class="inner"> 
			<h2>Edit Ship To</h2>
			<div class="content">
				<p>Update the Ship To address for this order only...</p>
				<label>Company Name</label>
				<input type="text" v-model="editingShipTo.company_name" />
				<label>Address 1</label>
				<input type="text" v-model="editingShipTo.address_1" />
				<label>Address 2</label>
				<input type="text" v-model="editingShipTo.address_2" />
				<div class="two-columns">
					<div>
						<label>City</label>
						<input type="text" v-model="editingShipTo.city" />
					</div>
					<div class="two-columns">
						<div>
							<label>State</label>
							<input type="text" v-model="editingShipTo.state" />
						</div>
						<div>
							<label>Zip Code</label>
							<input type="text" v-model="editingShipTo.zip_code" />
						</div>
					</div>
				</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showEditShipTo = false">Cancel</div>
				<div :class="['button', 'spaced', saving ? 'disabled' : 'action']" @click="orderShipTo = Object.assign({}, editingShipTo); showEditShipTo = false">Update</div>
			</div>
		</div>
	</div>

</div>
</template>

<script>
import Loading from '@/components/Loading.vue'
export default {
	name: 'reorder-cart',
	props: ['distributor_id'],
    components: {
        'loading': Loading
    },
	data() {
		return {
            loading: false,
			message: '',
			messageStatus: '',
			messageTimeout: 0,
			storeAdministrator: false,
			showPagination: false,
			clientId: '',
			filters: {
				page: 1,
				search_term: '',
				distributor_id: '',
				department_id: '',
				cart_user_id: '',
				stock: 'all',
				save_for_later: false
			},
			legacyLink: '',
			distributors: [],
			distributorId: '',
			departments: [],
			managerCarts: [],
			list: [],
			paging: {},
			showDetail: false,
			productDetail: {},
			showOrderPoints: false,
			orderPoints: {},
			showReviewOrder: false,
			orderDistributor: {},
			orderShipTo: {},
			confirmPlaceOrder: false,
			placeOrderHeading: '',
			placeOrderMessage: '',
			saving: false,
			showReorderCartSettings: false,
			showMergeReorderCarts: false,
			singleCartUserId: '',
			reorderCartMode: 'multi-cart',
			mergeReorderCartInventoryCount: 0,
			mergeReorderCartUserIds: new Set(),
			mergeReorderCartInventories: {},
			modalMessage: '',
			showSaveLater: false,
			selectedItems: {},
			costCompareItem: null,
			loadingCostCompare: false,
			costCompare: [],
			onOrderItem: null, 
			reviewItems: [],
			shoppingCartItemId: null,
			editingShipTo: {},
			showEditShipTo: false,
			confirmBulkActions: {
				show: false,
				action: '',
				productIds: [], 
				count: 0, 
				title: '',
				message: '', 
				actionCall: ''
			}, 
			confirmMerge: {
				show: false, 
				mode: '', 
				title: '', 
				message: '', 
				actionCall: ''
			}
		}
	},
    created() {
		this.getDistributors()
    },
	computed: {
		hasFilters: function() {
			return this.filters.search_term || this.filters.department_id || this.filters.stock
		},
		currentUserCartCount() {
			for (let i = 0; i < this.managerCarts.length; i++) {
				if (this.managerCarts[i]['user_id'] == this.filters.cart_user_id) {
					return this.managerCarts[i]['cart_count']
				}
			}
			return 0
		},
		distributorLabelFromId() {
			for (let i = 0; i < this.distributors.length; i++) {
				if (this.distributors[i]['distributor_id'] == this.distributorId) {
					return this.distributors[i]['label']
				}
			}
			return ''
		},
		itemsSelected() {
			let count = 0
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i]['selected']) {
					count++
				}
			}
			return count
		},
		itemByShoppingCartItemId() {
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i]['shopping_cart_item_id'] === this.shoppingCartItemId) {
					return this.list[i]
				}
			}
			return {}
		},
		// for show merge modal - exclude selected manager for modal display
		sortedReorderCarts() {
    		return this.managerCarts
      			.filter(manager => manager.user_id !== this.filters.cart_user_id)
      			.sort((a, b) => b.cart_count - a.cart_count);
  		}, 
		displayedList() {
			if (this.filters.stock === 'selected') {
				return this.list.filter(it => !!it.selected)
			}
			return this.list
		}, 
		selectedItemsDistributor() {
			return this.readSelectionPool()
		}, 
		selectedDistributorItemCount() {
			return Object.keys(this.selectedItemsDistributor).length
		},
		selectedDistributorQtyTotal() {
			let total = 0
			for (const id in this.selectedItemsDistributor) {
				if (this.selectedItemsDistributor[id].available > 0) {
					total += this.selectedItemsDistributor[id].quantity
				}
			}
			return total
		}, 
		selectedDistributorItemCost() {
			let cost = 0 
			for (const id in this.selectedItemsDistributor) {
				if (this.selectedItemsDistributor[id].available > 0) {
					cost += (this.selectedItemsDistributor[id].quantity * this.selectedItemsDistributor[id].cost)
				}
			}
			return cost.toFixed(2)
		}, 
		hasSelectedCurrent() {
			if (this.selectedDistributorQtyTotal > 0) {
				return this.selectedDistributorItemCount > 0
			}
		}, 
		allSelectedOnPage() {
			if(!this.displayedList.length) return false
			return this.displayedList.every(row => !!this.isSelected(row.product_id))
		}, 
		someSelectedOnPage() {
			if(!this.displayedList.length) return false
			return this.displayedList.some(row => !!this.isSelected(row.product_id))
		}, 
		reviewList() {
			return this.showReviewOrder && this.reviewItems.length ? this.reviewItems : this.list
		}
	},
	watch: {
		'filters.save_for_later': function() {
			this.getList()
		}
	},
	methods: {
		// editShipTo() {
		// 	this.editingShipTo = Object.assign({}, this.orderShipTo)
		// 	this.showEditShipTo = true
		// },
		getDistributors() {
			this.apiCall({ destination: 'get_list_client_distributors_for_inventory' }).then(obj => {
				this.distributors = obj.distributors
				this.getDistributor(this.distributors[0]['distributor_id'])
				this.storeAdministrator = obj.user.administrator_flag
				if (this.storeAdministrator) {
					this.getManagerCarts()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getDepartments() {
			this.apiCall({ destination: 'get_list_departments' }).then(obj => {
				this.departments = obj.departments
			}).catch(err => {
				console.log(err)
			})
		},
		getManagerCarts() {
			this.apiCall({ destination: 'get_list_manager_carts' }).then(obj => {
				this.managerCarts = obj.manager_carts
				this.singleCartUserId = obj.single_reorder_cart_user_id
				if (obj.legacy_link) {
					this.legacyLink = obj.legacy_link
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getDistributor(distributorId) {
			if (this.loading) { return }
			if (distributorId != this.distributorId) {
				this.distributorId = distributorId
				this.getList()
			}
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.getList()
		},
		getList() {
			if (this.loading) { return }
            this.loading = true
			this.showDetail = false
			this.filters.distributor_id = this.distributorId
			this.apiCall({ destination: 'get_list_reorder_cart', data: this.filters }).then(obj => {
                this.loading = false
				this.list = obj.list
				this.departments = obj.departments
				if (obj.paging) {
					this.paging = obj.paging
				}
				if (obj.cart_user_id) {
					this.filters.cart_user_id = obj.cart_user_id
				}
				if (this.singleCartUserId == this.filters.cart_user_id) {
					this.updateCustomRouteName('Reorder Cart')
				} else {
					this.updateCustomRouteName(this.getUser.user_name + ' Reorder Cart')
				}
				this.clientId = obj.client_id
				this.showPagination = true

				// NOTE: this cleans up selection pool to remove any items no longer in the list
				// const pool = this.readSelectionPool()
				// const validIds = new Set(this.list.map( r=> r.product_id ))
				// Object.keys(pool).forEach(pid => {
				// 	if (!validIds.has(String(pid))) {
				// 		this.$delete(pool, pid)
				// 	}
				// })

				this.syncSelectionToList()
			}).catch(err => {
                this.loading = false
				console.log(err)
			})
		},
		getCostCompare(productId) {
			if (this.loadingCostCompare || !this.distributorId) { return }
			if (this.costCompareItem == productId) {
				this.costCompareItem = null
				return
			}
			this.loadingCostCompare = true
			this.costCompareItem = productId
			this.apiCall({ destination: 'get_list_cost_compare', data: { distributors: this.distributors, product_id: productId } }).then(obj => {
				this.loadingCostCompare = false
				this.costCompare = obj.list
			}).catch(err => {
				this.loadingCostCompare = false
				console.log(err)
			})
		},
		showOnOrder(productId) {
			if (this.onOrderItem == productId) {
				this.onOrderItem = null
			} else {
				this.onOrderItem = productId
			}
		},
		saveQuantity(productId) {
			if (this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'save_manager_cart_quantity', data: { cart_user_id: this.filters.cart_user_id, product_id: productId, quantity: this.list.find(item => item.product_id === productId).quantity } }).then(obj => {
				this.saving = false
			}).catch(err => {
				console.log(err)
			})
		},
		manageItemNotes(shoppingCartItemId) {
			this.shoppingCartItemId = shoppingCartItemId
		},
		saveItemNotes() {
			if (!this.shoppingCartItemId || this.saving) { return }
			this.saving = true
			this.apiCall({ destination: 'save_cart_item_notes', data: { shopping_cart_item_id: this.shoppingCartItemId, notes: this.itemByShoppingCartItemId.notes } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.message = 'Notes saved!'
					this.messageStatus = 'success'
					this.messageTimeout = 2000
					this.shoppingCartItemId = null
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeFromCart(which) {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Removing...'
			this.messageStatus = ''
			this.messageTimeout = 0
			let productIds = []
			if (Array.isArray(which)) {
				productIds = which
			} else {
				productIds.push(which)
			}
			this.apiCall({ destination: 'remove_from_manager_carts', data: { cart_user_id: this.filters.cart_user_id, product_ids: productIds, remove_from_wishlist: this.filters.save_for_later } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Item' + (productIds.length > 1 ? 's' : '') + ' removed!'
					this.messageTimeout = 2000
					this.showOrderPoints = false
					// remove productIds from selection pool
					const pool = this.readSelectionPool()
					productIds.forEach(pid => {
						this.$delete(pool, String(pid))
					})
					this.getList()
					this.getManagerCarts()
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		removeSelected() {
			if (!this.hasSelected) { return }
			let productIds = []
			for (let i = 0; i < this.list.length; i++) {
				if (this.list[i]['selected']) {
					productIds.push(this.list[i]['product_id'])
				}
			}
			this.removeFromCart(productIds)
		},
		removeFiltered() {
			if (this.list.length < 1) { return }
			let productIds = []
			for (let i = 0; i < this.list.length; i++) {
				productIds.push(this.list[i]['product_id'])
			}
			this.removeFromCart(productIds)
		},
		removeAll() {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'remove_from_manager_carts', data: { cart_user_id: this.filters.cart_user_id, product_ids: 'all' } }).then(obj => {
				this.saving = false
				this.message = obj.message
				if (obj.status == 'success') {
					this.getList()
					this.getManagerCarts()
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setSaveForLater(products) {
			if (this.saving) { return }
			this.saving = true
			if (this.filters.save_for_later) {
				this.message = 'Moving to cart...'
			} else {
				this.message = 'Saving for later...'
			}
			this.messageStatus = ''
			this.messageTimeout = 0
			let productIds = []
			if (Array.isArray(products)) {
				productIds = products
			} else {
				productIds.push(products)
			}
			this.apiCall({ destination: 'set_manager_wish_list', data: { cart_user_id: this.filters.cart_user_id, product_ids: productIds, move_to_cart: this.filters.save_for_later } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
				if (this.filters.save_for_later) {
						this.message = 'Item' + (productIds.length > 1 ? 's' : '') + ' moved to cart!'
					} else {
						this.message = 'Item' + (productIds.length > 1 ? 's' : '') + ' saved for later!'
					}
					this.messageTimeout = 2000
					this.showOrderPoints = false
					this.getList()
					this.getManagerCarts()
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		reviewOrder() {
			if (this.showReviewOrder || !this.distributorId) return 
			
			const pool = this.readSelectionPool()
			const ids = Object.keys(pool)
			if (!ids.length) return

			const byId = new Map(this.list.map(r => [String(r.product_id), r]))
			this.reviewItems = ids.map(pid => {
				const rec = pool[pid] || {}
				const row = byId.get(String(pid)) || {}
				return {
					product_id: Number(pid), 
					description: rec.description || row.description || '',
					item_number: rec.item_number || row.item_number || '',
					cost: Number(rec.cost || row.cost || '0.00'),
					price: Number(rec.price || row.price || '0.00'),
					margin: Number(rec.margin || row.margin || '0.00'),
					quantity: Number(rec.quantity || row.quantity || 0),
					available: Number(rec.available || row.available || 0)
				}
			}).filter(x => x.quantity > 0 && x.available > 0)

			this.apiCall({destination: 'get_reorder_contact_info', data: {distributor_id: this.distributorId}}).then(obj => {
					this.orderDistributor = obj.order_distributor
					this.orderShipTo = obj.order_ship_to
					this.showReviewOrder = true
			}).catch(err => {
				console.log(err)
			})
		},
		orderTotal(column) {
			let total = 0
			for (let i = 0; i < this.list.length; i++) {
				total += parseFloat(this.list[i][column]) * parseFloat(this.list[i]['quantity'])
			}
			return total.toFixed(2)
		},
		orderQuantityTotal() {
			let total = 0
			for (let i = 0; i < this.list.length; i++) {
				total += parseInt(this.list[i]['quantity'])
			}
			return total
		},
		getDetail(productId) {
			this.apiCall({ destination: 'get_item_product_by_id', data: { product_id: productId } }).then(obj => {
				if (obj.status == 'success') {
					this.productDetail = obj.product_detail
					this.showDetail = true
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getManager(managerId) {
			if (this.managerCarts.length > 0) {
				return this.managerCarts.find(manager => {
					return manager.user_id == managerId
				})
			}
			return 0
		},
		setReorderPoints(item) {
			this.orderPoints = item
			this.showOrderPoints = true
		},
		saveOrderPoints() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving...'
			this.messageStatus = ''
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_product_reorder_actions', data: { product_id: this.orderPoints.product_id, reorder_point: this.orderPoints.reorder_point, order_quantity: this.orderPoints.order_quantity } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Order points saved!'
					this.messageTimeout = 2000
					this.showOrderPoints = false
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		mergeSelectedCarts() {
			if (this.saving) { return }
			if (this.mergeReorderCartUserIds.size < 1) { return }
			this.saving = true
			this.message = ''
			const selectedUserIds = Array.from(this.mergeReorderCartUserIds);
			this.apiCall({ destination: 'merge_selected_manager_carts', data: { cart_user_id: this.filters.cart_user_id, selected_user_ids: selectedUserIds} }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.message = 'Carts merged successfully!'
					// set timeout
					setTimeout(() => {
						this.message = ''
						this.showMergeReorderCarts = false
					}, 2000)
					this.getList()
					this.getManagerCarts()
				} else {
					this.message = 'Oops! ' + obj.message
				}
			}).catch(err => {
				console.log(err)
			})
			this.toggleModalMergeReorderCart()
		},
		mergeAllCarts() {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'merge_manager_carts', data: { cart_user_id: this.filters.cart_user_id } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.message = 'Carts merged successfully!'
					// set timeout
					setTimeout(() => {
						this.message = ''
						this.showMergeReorderCarts = false
					}, 2000)
					this.getList()
					this.getManagerCarts()
				} else {
					this.message = 'Oops! ' + obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		emptyAllCarts() {
			if (this.saving) { return }
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'remove_from_manager_carts', data: { cart_user_id: 'all', product_ids: 'all' } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					// this.showConfirm = false
					this.getList()
					this.getManagerCarts()
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.showDetail = false
			this.detail = {}
			this.showPagination = true
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		clearFilters() {
			this.filters.page = 1
			this.filters.search_term = ''
			this.filters.department_id = ''
			this.filters.stock = 'all'
			this.getList()
		},
		startPlaceOrder() {
			const items = this.reviewItems.length ? this.reviewItems : this.list
			const totalCost = this.orderTotalFrom(items, 'cost')
			this.placeOrderHeading = "Place Order"
			this.placeOrderMessage = "You are about to place an order with<br>" + this.orderDistributor.company_name + " for $" + totalCost + "."
			this.confirmPlaceOrder = true
		},
		placeOrder() {
			if (this.saving) { return }
			const items = this.reviewItems.length ? this.reviewItems : this.list
			const totalCost = this.orderTotalFrom(items, 'cost')
			this.placeOrderHeading = "Submitting Order"
			this.placeOrderMessage = "Submitting order to<br>" + this.orderDistributor.company_name + " for $" + totalCost + "."
			this.saving = true
			this.message = ''
			this.apiCall({ destination: 'create_distributor_order', data: { cart_user_id: this.filters.cart_user_id, distributor_id: this.distributorId, ship_to: this.orderShipTo, items: items } }).then(obj => {
				if (obj.status == 'success') {
					this.placeOrderHeading = "Success"
					this.placeOrderMessage = "Order submitted!<br><br>" + obj.message
					setTimeout(() => {
						this.saving = false
						this.showReviewOrder = false
						this.confirmPlaceOrder = false
						this.placeOrderHeading = ''
						this.placeOrderMessage = ''
						this.unselectAllVisible()
						this.getList()
						this.getManagerCarts()
					}, 2500)
				} else {
					this.placeOrderHeading = "Error"
					this.placeOrderMessage = "Order not submitted...<br><br>" + obj.message
					this.saving = false
				}
			}).catch(err => {
				console.log(err)
				this.saving = false
			})
		},
		toggleModalReorderCartSettings() {
			this.modalMessage = ''
			this.showReorderCartSettings = !this.showReorderCartSettings
			this.reorderCartMode = this.singleCartUserId ? 'single-cart' : 'multi-cart';
		},
		toggleModalMergeReorderCart() {
			if (this.filters.cart_user_id === '') { return }
			this.modalMessage = ''

			this.mergeReorderCartInventories = Object.fromEntries(
  				this.managerCarts.map(manager => [manager.user_id, manager.cart_count])
			);
			this.mergeReorderCartUserIds.clear(),
			this.showMergeReorderCarts = !this.showMergeReorderCarts
			if (this.singleCartUserId) {
				// iterate all manager carts except current and add to set
				this.mergeReorderCartInventoryCount = 0
				this.managerCarts.forEach(manager => {
					if (manager.user_id !== this.filters.cart_user_id && manager.cart_count > 0) {
						this.mergeReorderCartUserIds.add(manager.user_id)
						this.mergeReorderCartInventoryCount += manager.cart_count
						this.mergeReorderCartInventories[manager.user_id] = 0
					}
				})
			} else {
				if (this.filters.cart_user_id) {
					const manager = this.getManager(this.filters.cart_user_id)
					if (manager) {
						this.mergeReorderCartInventoryCount = manager.cart_count
					} 
				} else {
					this.mergeReorderCartInventoryCount = 0
				}
			}
		},
		updateMergeReorderCart(manager, isChecked) {
			if (manager.cart_count == 0) { return }
			if (isChecked) {
				this.mergeReorderCartInventoryCount += manager.cart_count		
				this.mergeReorderCartUserIds.add(manager.user_id)
				this.mergeReorderCartInventories[manager.user_id] -= manager.cart_count
			} else {
				this.mergeReorderCartInventoryCount -= manager.cart_count
				this.mergeReorderCartUserIds.delete(manager.user_id)
				this.mergeReorderCartInventories[manager.user_id] += manager.cart_count
			}
		},
		resetSingleReorderCartMerge() {
			this.reorderCartMode = 'multi-cart'
			this.singleCartUserId = ''
			if (this.managerCarts.length > 0) {
				this.filters.cart_user_id = this.managerCarts[0].user_id
			}
			this.showMergeReorderCarts = false
			this.showReorderCartSettings = false
			this.saveReorderCartSettings()
		},
		saveReorderCartSettings() {
			if (this.saving) { return }
			// guard if same settings as saved
			if ((this.reorderCartMode === 'single-cart' && this.singleCartUserId) ||
				 this.reorderCartMode === 'multi-cart' && this.singleCartUserId === "") { 
				 this.showReorderCartSettings = false
				return 
			}

			this.modalMessage = ''
			this.saving = true
			this.message = 'Saving...'
			this.messageStatus = ''
			this.messageTimeout = 0

			this.apiCall({ destination: 'save_reorder_cart_settings', data: { reorder_cart_mode: this.reorderCartMode } }).then(obj => {
				this.saving = false
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Reorder cart settings saved!'
					if (obj.single_reorder_cart_user_id !== '') {
						this.singleCartUserId = obj.single_reorder_cart_user_id
						this.filters.cart_user_id = this.singleCartUserId
						this.toggleModalMergeReorderCart()
					} else {
						this.singleCartUserId = ''
						if (obj.cart_user_id) {
							this.filters.cart_user_id = obj.cart_user_id
						}
					}
					this.getList()
					this.getManagerCarts()
					this.messageTimeout = 2000
				} else {
					this.message = obj.message
				}
				this.toggleModalReorderCartSettings()
			}).catch(err => {
				console.log(err)
			})
		},
		getSelectionPool(cartId = this.filters.cart_user_id, distId = this.distributorId, tab = this.tabView()) {
			if (!this.selectedItems[cartId]) {
				this.$set(this.selectedItems, cartId, {})
			}
			if (!this.selectedItems[cartId][distId]) {
				this.$set(this.selectedItems[cartId], distId, {})
			}
			if (!this.selectedItems[cartId][distId][tab]) {
				this.$set(this.selectedItems[cartId][distId], tab, {})
			}
			return this.selectedItems[cartId][distId][tab]
		},
		readSelectionPool(cartId = this.filters.cart_user_id, distId = this.distributorId, tab = this.tabView()) {
			return (this.selectedItems[cartId] && this.selectedItems[cartId][distId] && this.selectedItems[cartId][distId][tab]) || {}
		},
		addSelected(item) {
			const products = this.getSelectionPool()
			this.$set(products, item.product_id, {
				available: Number(item.available) || 0,
				quantity: Number(item.quantity) || 0,
				cost: Number(item.cost) || 0,
				price: Number(item.price) || 0,
				margin: Number(item.margin) || 0,
				description: item.description,
				item_number: item.item_number
			})
			
		}, 
		removeSelectedById(productId) {
			const products = this.readSelectionPool()
			if (!products[productId]) return
			this.$delete(products, productId)
		}, 
		isSelected(productId){
			const product = this.readSelectionPool()
			return !!product[productId]
		}, 
		toggleSelected(item, checked){
			this.$set(item, 'selected', !!checked)
			if (checked) {
				if (item.quantity == null || item.quantity === '' || Number(item.quantity) === 0 ){
					this.$set(item, 'quantity', 1)
				}
				this.addSelected(item)
			} else {
				this.removeSelectedById(item.product_id)
			}
		}, 
		updateSelectedQuantity(item){
			const products = this.readSelectionPool()
			if (!products[item.product_id]) return
			products[item.product_id].quantity = Number(item.quantity) || 0
		}, 
		syncSelectionToList() {
			const products = this.readSelectionPool()
			this.list.forEach(row => {
				const rec = products[row.product_id]
				const isSelected = !!rec

				this.$set(row, 'selected', isSelected)

				if (isSelected && rec.quantity != null) {
					this.$set(row, 'quantity', rec.quantity)
				}

				if (isSelected){
					rec.cost = Number(row.cost) || rec.cost
					rec.price = Number(row.price) || rec.price
					rec.margin = Number(row.margin) || rec.margin
					rec.description = row.description || rec.description
					rec.item_number = row.item_number || rec.item_number
				}
			})
		}, 
		selectAll(){
			const products = this.getSelectionPool()
			this.displayedList.forEach(row => {
				if (row.quantity == null || row.quantity === '' || Number(row.quantity) === 0) {
					this.$set(row, 'quantity', 1)
				}
				this.$set(row, 'selected', true)
				this.$set(products, row.product_id, {
					available: Number(row.available) || 0,
					quantity: Number(row.quantity) || 0,
					cost: Number(row.cost) || 0,
					price: Number(row.price) || 0,
					margin: Number(row.margin) || 0,
					description: row.description,
					item_number: row.item_number
				})

			})
		}, 
		clearSelectionsForDistributor(){
			const cartId = this.filters.cart_user_id
			const distId = this.distributorId

			if (this.selectedItems[cartId] && this.selectedItems[cartId][distId]){
				this.$set(this.selectedItems[cartId], distId, {})
			}
			this.list.forEach(row => { this.$set(row, 'selected', false) })
		}, 
		toggleSelectAll(checked){
			if(checked) this.selectAll()
			else this.unselectAllVisible()
		},
		unselectAllVisible(){
			const products = this.readSelectionPool()
			this.displayedList.forEach(row => {
				if (products[row.product_id]) this.$delete(products, row.product_id)
				this.$set(row, 'selected', false)
			})
		}, 
		getSelectedProductIdsFromList() {
			const ids = []
			for (let i = 0; i < this.list.length; i++){
				if (this.list[i].selected) {
					ids.push(this.list[i].product_id)
				}
			}
			return ids
		}, 
		bulkSaveSelectedForLater() {
			const ids = this.getSelectedProductIdsFromList()
			if(!ids.length || this.saving) return
			this.setSaveForLater(ids)
		}, 
		bulkRemoveSelected() {
			const ids = this.getSelectedProductIdsFromList()
			if(!ids.length || this.saving) return
			this.removeFromCart(ids)
		}, 
		bulkMoveSelectedToCart() {
			const ids = this.getSelectedProductIdsFromList()
			if(!ids.length || this.saving) return
			this.setSaveForLater(ids)
		},
		orderTotalFrom(items, column) {
			let total = 0 
			for (let i =0; i< items.length; i++) {
				const qty = Number(items[i].quantity) || 0
				const val = Number(items[i][column]) || 0
				total += qty * val
			}
			return total.toFixed(2)
		}, 
		openBulkActionsConfirmation(action, productIds) {
			if (this.saving) return; 
			const count = Array.isArray(productIds) ? productIds.length : 0; 

			let title = 'Confirm Bulk Action';
			let actionCall = 'Continue';
			let message = '';

			if (action === 'save'){
				title = 'Save Selected Items for Later';
				actionCall = 'Save';
				message = `Are you sure you want to save ${count} selected item${count > 1 ? 's' : ''} for later?`;
			} else if (action === 'move'){
				title = 'Move Selected Items to Cart';
				actionCall = 'Move';
				message = `Are you sure you want to move ${count} selected item${count > 1 ? 's' : ''} to the cart?`;
			} else if (action === 'remove'){
				title = 'Remove Selected Items';
				actionCall = 'Remove';
				message = `Are you sure you want to remove ${count} selected item${count > 1 ? 's' : ''} from the cart? THIS CANNOT BE UNDONE.`;
			}

			this.confirmBulkActions = {
				show: true, 
				action, 
				productIds,
				count,
				title,
				message,
				actionCall
			};
		}, 
		closeBulkActionsConfirmation() {
			this.confirmBulkActions.show = false;
		},
		async executeBulkAction() {
			if (this.saving) return;

			const {action, productIds} = this.confirmBulkActions;

			if (action === 'remove'){
				this.closeBulkActionsConfirmation();
				this.bulkRemoveSelected();
			} else if (action === 'save'){
				this.closeBulkActionsConfirmation();
				this.bulkSaveSelectedForLater();
			} else if (action === 'move'){
				this.closeBulkActionsConfirmation();
				this.bulkMoveSelectedToCart();
			}
		}, 
		tabView() {
			return this.filters.save_for_later ? 'wishlist' : 'cart'
		}, 
		openMergeConfirmation(mode) {
			if (this.saving) return; 
			
			if (mode === 'selected' && this.mergeReorderCartUserIds.size < 1) return; 

			let title = 'Merge Reorder Carts'; 
			let actionCall = 'Yes, Merge';
			let message = '';

			if (mode === 'all') {
				message = `Are you Sure you want to merge<br><b>all manager reorder carts</b><br>into a Single Reorder Cart</b>?<br><br>Once done, the old carts cannot be recovered.`
			} else if (mode === 'selected') {
				const countSelected = this.mergeReorderCartUserIds.size;
				const currentManager = this.getManager(this.filters.cart_user_id);
				const currentName = currentManager ? currentManager.user_name : 'your cart'; 
				message = `Please confirm that you want to merge<br><b>${countSelected}</b> selected cart${countSelected > 1 ? 's' : ''} into <b>${currentName}</b>'s cart?<br><br>Once merged, those carts cannot be recovered.`
			}

			this.confirmMerge = {
				show: true, 
				mode, 
				title, 
				message, 
				actionCall
			}
		}, 
		executeMergeReorderCarts() {
			if(this.saving) return; 
			const mode = this.confirmMerge.mode;

			this.confirmMerge.show = false;

			if (mode === 'all'){
				this.mergeAllCarts();
			} else if (mode === 'selected'){
				this.mergeSelectedCarts();
			}
		}
		

	}
}
</script>

<style lang="scss" scoped>
.list-nav {
	margin: 0 1em 0.5em 1em;
	border-bottom: 1px solid #ccc;
	.nav-tab { 
		display: inline-block; 
		margin: 0 0.5em; 
		padding: 0.5em 1.25em; 
		border: 1px solid #ccc; 
		border-bottom: none; 
		border-radius: 0.25em 0.25em 0 0; 
		cursor: pointer; 
		&.nav-tab-active { 
			background: #237F59; 
			color: white;
			font-weight: bold;
		}
	}
	.bulk-controls {
		float: right;
		.bulk-cart-control {
			display: inline;
			margin-left: 1.75em;
			color: #237F59;
			text-decoration: underline;
			cursor: pointer;
			&:hover {
				color: #08b76e;
			}
		}
	}
	//.controls { display: inline-block; float: right; .button { margin-left: 0.5em; } }
}
.list {
	padding-bottom: 12em !important;
}

.cart-data-grid {
	margin: 1em;
	padding-bottom: 0.75em;
	border-bottom: 1px solid #ccc;
	display: grid;
	grid-template-columns: 7em 1fr 1fr 0.66fr 1fr 1fr 8em;
	grid-column-gap: 1em;
	align-items: center;
	.cart-data-grid-heading {
		grid-column: 1 / span 7;
		.title { display: inline-block; }
	}
	.cart-data-image {
		text-align: center;
		//padding: 1em 0;
		img {
			max-width: 100%;
			max-height: 4em;
			cursor: pointer;
		}
	}
	label { padding: 0 0.5em 0 0; }
	.item-checkbox {
		font-size: 0.8em;
		margin-bottom: 0.25em;
		span { padding: 0.66em 0.25em 0.66em 0.66em; }
	}
	.title {
		font-weight: bold;
		margin: 0 0 0.25em 0;
		cursor: pointer;
	}
	.unavailabletext {
		color: #999;
	}
	.available {
		text-align: center;
		font-weight: bold;
		label { display: block; font-size: 0.8em; padding: 0; font-weight: normal; }
	}
	.cart-data-grid-compare, .cart-data-grid-on-order {
		position: absolute;
		top: 1.75em;
		left: -12em;
		width: 30em;
		background: white;
		border: 1px solid #ccc;
		z-index: 1000;
		box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
		h3 {
			font-size: 0.8em;
			margin: 0 0 0.5em 0;
			background-color: #237F59;
			color: #fff;
			padding: 0.25em 0.5em;
			text-transform: uppercase;
			.close {
				float: right;
				cursor: pointer;
				font-weight: bold;
			}
		}
		.compare-table {
			font-size: 0.9em;
			th {
				font-size: 0.9em;
				background: none;
				text-transform: none;
				font-weight: normal;
				padding: 0 0.5em;
			}
			td {
				border-top: 1px solid #ccc;
				padding: 0.25em 0.5em;
			}
			tr.selected td {
				font-weight: bold;
			}
		}
	}
	.cart-data-grid-on-order {
		width: 20em;
		left: -9em;
	}
}
table.data-table {
	td.label { padding: 0 0.66em 0 0; }
	td.data { padding: 0.25em 0; }
	input { font-size: 0.8em; width: 5em; margin: 0 1em 0 0; }

	td.unavailable { color: red; }
}
.review-order {
	grid-column: 1 / -1;
	margin: 0 0 0 1em;
	.review-order-section {
		height: 9em;
		margin-bottom: 1em;
		td { padding: 0.25em 0; }
	}
	.order-data-grid {
		tbody {
			tr:nth-child(even) {
				background-color: #f1f7f3;
			}
			tr:last-child {
				border-top: 1px solid black;
			}
		}
	}
	.controls {
		margin: 1em 0;
		padding: 1em 0 0 1em;
		border-top: 1px solid #ccc;
		justify-items: auto;
	}
}
.confirm-order {
	.content {
		height: 7em;
		width: 30em;
	}
}

.inner.modal-lg {
	border: 2px solid darkgray;
	max-width: 55rem;
	.divider {
		background-color: lightgray;
		padding: 0;
		margin: 1rem 0;
	}
	.two-columns {
		gap: 5rem;
		> p {
			text-align: right;
		}
		.header-cart {
			gap: 1rem;
			font-size: 18px;
			padding-right: 1rem;
		} 
		.header-cart-txt {
			text-align: left;
			margin-bottom: 0;
		}
		.single {
			margin-bottom: 1rem;
		}
		.header-cart-instructions {
			padding-right: 1rem;
			margin: 1rem 0;
		}
	}
	.two-columns.merge-cart {
		gap: 1.5rem;
		.col-1 {
			border-right: 1px solid lightgray;
		}
	}
	.option-block {
		margin-bottom: .75rem;
		label {
			font-size: 1.25rem;
			margin-left: .75rem;
		}
		h3 {
			font-weight: normal;
			font-size: 1.25rem;
			margin-top: 1.5rem;
		}
		button {
			background-color: #999;
		}
	}
	.merge-cart-line {
		font-size: 16px;
		display: grid;
		grid-template-columns: 2fr 1fr;
		padding-right: 1rem;
		align-items: center;
		> p {
			text-align: right;
		}
		.user-line {
			display: flex;
			flex-direction: row;
			align-items: center;
			height: 2.5em;
		}
	}
	.modal-message {
		padding-bottom: 1rem;
		font-weight: bold;
		font-style: italic;
	}
	.confirm-bulk .content {
		min-height: 5.5em;
		width: 30em;
	}
}
</style>
